import { useRef, useState } from 'react';
import { ConfigurationStatus } from '../domain/enums';
import { Configuration } from '../domain/types';
import {
  ConfigurationMutationOptions,
  useUpdateConfigurationStatus,
} from './use-configuration-mutations';

export const STATUS_TRANSITION_ERROR_MESSAGE =
  'Could not change this configuration’s status. Please try again.';

export interface PendingStatusTransition {
  configuration: Configuration;
  target: ConfigurationStatus;
}

export interface UseConfigurationStatusTransitionReturn {
  /** The transition awaiting confirmation, or `null` when no dialog is open. */
  pendingTransition: PendingStatusTransition | null;
  selectTransition: (
    configuration: Configuration,
    target: ConfigurationStatus,
  ) => void;
  cancelTransition: () => void;
  confirmTransition: () => Promise<void>;
  isSaving: boolean;
  error: string | null;
}

/**
 * Drives the confirm-then-transition flow shared by the configurations list
 * and the configuration detail page: hold the operator's choice, run the
 * mutation once they confirm, and keep the dialog open with an error if it
 * fails so the attempt is not silently lost.
 */
export const useConfigurationStatusTransition = ({
  onSuccess,
}: ConfigurationMutationOptions = {}): UseConfigurationStatusTransitionReturn => {
  const [pendingTransition, setPendingTransition] =
    useState<PendingStatusTransition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { updateConfigurationStatus, isSaving } = useUpdateConfigurationStatus({
    onSuccess,
  });

  // `isSaving` only flips true a tick after the click, so a fast double
  // confirm would otherwise fire the mutation twice.
  const isSubmittingRef = useRef(false);

  const selectTransition = (
    configuration: Configuration,
    target: ConfigurationStatus,
  ): void => {
    setError(null);
    setPendingTransition({ configuration, target });
  };

  const cancelTransition = (): void => {
    setError(null);
    setPendingTransition(null);
  };

  const confirmTransition = async (): Promise<void> => {
    if (!pendingTransition || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      await updateConfigurationStatus(
        pendingTransition.configuration.id,
        pendingTransition.target,
      );
      setError(null);
      setPendingTransition(null);
    } catch {
      setError(STATUS_TRANSITION_ERROR_MESSAGE);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return {
    pendingTransition,
    selectTransition,
    cancelTransition,
    confirmTransition,
    isSaving,
    error,
  };
};
